import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Trophy } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion/Reveal";
import { useMachines } from "@/components/machines/MachineProvider";
import { MACHINES } from "@/data/site";
import { cn } from "@/lib/utils";

const ClawGame3D = lazy(() => import("@/components/three/ClawGame3D"));
const loadNames = () => import("@/components/three/ClawGame3D").then((m) => m.prizeName);

const STATUS = { idle: "Move the claw, then press DROP", drop: "Dropping…", grab: "Grabbing…", lift: "Got one!", carry: "Bringing it home…", release: "Releasing…", won: "WINNER!" };
const KEYS = { ArrowLeft: [-1, 0], a: [-1, 0], ArrowRight: [1, 0], d: [1, 0], ArrowUp: [0, -1], w: [0, -1], ArrowDown: [0, 1], s: [0, 1] };

const Pad = ({ dir, icon: Icon, game, id, className }) => {
  const press = (on) => (e) => {
    e.preventDefault();
    if (on) e.currentTarget.setPointerCapture?.(e.pointerId);
    game.current.dir = on ? { x: dir[0], z: dir[1] } : { x: 0, z: 0 };
  };
  return (
    <button onPointerDown={press(true)} onPointerUp={press(false)} onPointerCancel={press(false)} onContextMenu={(e) => e.preventDefault()} className={cn("grid h-12 w-12 touch-none select-none place-items-center rounded-2xl border border-white/10 bg-ink-800/90 sm:h-14 sm:w-14 text-white transition-[background-color,border-color,transform] active:scale-95 active:border-volt active:bg-volt/15", className)} aria-label={id} data-testid={`claw-pad-${id}`}>
      <Icon className="h-5 w-5" />
    </button>
  );
};

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    {Array.from({ length: 36 }, (_, i) => (
      <motion.span key={i} className="absolute top-1/3 h-2.5 w-1.5 rounded-sm" style={{ left: "50%", background: ["#00F0FF", "#FF2A40", "#fde68a", "#fff"][i % 4] }} initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }} animate={{ x: (Math.random() - 0.5) * 700, y: 200 + Math.random() * 300, opacity: 0, rotate: Math.random() * 720 }} transition={{ duration: 1.6 + Math.random(), ease: [0.2, 0.8, 0.4, 1] }} />
    ))}
  </div>
);

export const ClawGame = () => {
  const game = useRef({ x: 0.1, z: -0.1, y: 2.06, open: 1, dir: { x: 0, z: 0 }, phase: "idle", t: 0, target: -1, held: -1, falling: -1, vy: 0, request: false });
  const [phase, setPhase] = useState("idle");
  const [wins, setWins] = useState(0);
  const [prize, setPrize] = useState(null);
  const { openRequest } = useMachines();
  const box = useRef(null);

  const onPhase = useCallback((p) => setPhase(p), []);
  const onWin = useCallback((c) => { setWins((w) => w + 1); loadNames().then((name) => setPrize(name(c))); }, []);
  const drop = () => { if (game.current.phase === "idle") game.current.request = true; };

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => window.dispatchEvent(new CustomEvent("claw-view", { detail: e.isIntersecting })), { threshold: 0.2 });
    io.observe(box.current);
    return () => { io.disconnect(); window.dispatchEvent(new CustomEvent("claw-view", { detail: false })); };
  }, []);

  useEffect(() => {
    if (!prize) return;
    const t = setTimeout(() => setPrize(null), 2600);
    return () => clearTimeout(t);
  }, [prize]);

  const onKey = (down) => (e) => {
    const k = KEYS[e.key];
    if (k) { e.preventDefault(); game.current.dir = down ? { x: k[0], z: k[1] } : { x: 0, z: 0 }; }
    if (down && (e.key === " " || e.key === "Enter")) { e.preventDefault(); drop(); }
  };

  return (
    <section id="claw-game" className="relative overflow-hidden border-t border-white/5 bg-ink-900 py-24 lg:py-32" data-testid="claw-game-section">
      <div className="pointer-events-none absolute right-[10%] top-1/3 h-[50vh] w-[50vh] rounded-full bg-signal/10 blur-[140px]" />
      <div className="container-x relative grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal><p className="eyebrow flex items-center gap-3"><span className="h-2 w-2 animate-pulse rounded-full bg-signal" /> Play it live</p></Reveal>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-white sm:text-5xl">
            <MaskLines onView lines={["Try the", <span key="c" className="text-outline">Premium Claw.</span>]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-base leading-relaxed text-slate-400">Steer the claw with the arrows (or your keyboard), hit <span className="font-semibold text-white">DROP</span>, and grab a prize. This is the kind of machine guests can't walk past.</p>
            <div className="mt-8 flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-800/60 p-4">
              <Trophy className="h-6 w-6 text-volt" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Prizes won</p>
                <p className="font-display text-3xl font-black tabular-nums text-white" data-testid="claw-wins">{wins}</p>
              </div>
            </div>
            <button onClick={() => openRequest(MACHINES.find((m) => m.id === "premium-claw"))} className="btn-ghost mt-6 h-12 px-6 text-sm" data-testid="claw-request-button">Put one in my business <ArrowRight className="btn-arrow h-4 w-4" /></button>
          </Reveal>
        </div>
        <div ref={box} tabIndex={0} onKeyDown={onKey(true)} onKeyUp={onKey(false)} onPointerDown={() => box.current?.focus({ preventScroll: true })} id="claw-game-box" className="relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-b from-ink-800/50 to-ink-950 outline-none focus-visible:border-volt/50 lg:col-span-8" data-testid="claw-game-box">
          <div className="relative h-[min(600px,78svh)] min-h-[440px]">
            <Suspense fallback={null}><ClawGame3D game={game} onPhase={onPhase} onWin={onWin} /></Suspense>
            <div className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 rounded-full border border-white/10 bg-ink-950/80 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-200 backdrop-blur-md" data-testid="claw-status">
              {STATUS[phase]}
            </div>
            <AnimatePresence>
              {prize && (
                <motion.div key={wins} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pointer-events-none absolute inset-0 grid place-items-center" data-testid="claw-win-overlay">
                  <Confetti />
                  <motion.div initial={{ scale: 0.4, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14 }} className="rounded-3xl border border-volt/40 bg-ink-950/85 px-8 py-6 text-center shadow-[0_0_80px_-10px_rgba(0,240,255,0.8)] backdrop-blur-xl">
                    <p className="font-display text-5xl font-black uppercase tracking-tight text-white sm:text-6xl">You win<span className="text-signal">!</span></p>
                    <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-volt">{prize} prize</p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-ink-950/90 via-ink-950/40 to-transparent p-4 pt-16 sm:p-6 sm:pt-20" data-testid="claw-controls">
            <div className="pointer-events-auto grid grid-cols-3 gap-1.5 rounded-3xl border border-white/10 bg-ink-950/60 p-2 backdrop-blur-xl sm:gap-2">
              <span />
              <Pad dir={[0, -1]} icon={ArrowUp} game={game} id="back" />
              <span />
              <Pad dir={[-1, 0]} icon={ArrowLeft} game={game} id="left" />
              <Pad dir={[0, 1]} icon={ArrowDown} game={game} id="forward" />
              <Pad dir={[1, 0]} icon={ArrowRight} game={game} id="right" />
            </div>
            <p className="mb-3 hidden text-center font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-slate-400 md:block">Keyboard: arrows / WASD<br />Space to drop</p>
            <motion.button whileTap={{ scale: 0.92 }} onClick={drop} disabled={phase !== "idle"} className="pointer-events-auto relative mb-2 grid h-20 w-20 shrink-0 place-items-center sm:h-24 sm:w-24 rounded-full bg-signal font-display text-lg font-black uppercase tracking-wider text-white shadow-[0_10px_0_#8a0f1d,0_0_50px_-5px_rgba(255,42,64,0.8)] transition-[opacity,transform,box-shadow] active:translate-y-[6px] active:shadow-[0_4px_0_#8a0f1d] disabled:opacity-50" data-testid="claw-drop-button">
              Drop
            </motion.button>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};
